import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowLeft, Upload, CheckCircle2, Video, Trash2 } from "lucide-react";
import wtaLogo from "@/assets/wta-logo.png";

type LessonRow = {
  slug: string;
  title: string;
  order_index: number;
  video_path: string | null;
  video_url: string | null;
};

const AdminLessons = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [lessons, setLessons] = useState<LessonRow[]>([]);
  const [uploadingSlug, setUploadingSlug] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login", { replace: true });
        return;
      }
      const { data: role } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (!role) {
        toast.error("Admin access required");
        navigate("/", { replace: true });
        return;
      }
      setIsAdmin(true);
      await loadLessons();
      setLoading(false);
    };
    init();
  }, [navigate]);

  const loadLessons = async () => {
    const { data, error } = await supabase
      .from("lessons")
      .select("slug, title, order_index, video_path, video_url")
      .order("order_index", { ascending: true });
    if (error) {
      toast.error("Failed to load lessons");
      return;
    }
    setLessons((data || []) as LessonRow[]);
  };

  const handleUpload = async (lesson: LessonRow, file: File) => {
    if (!file.type.startsWith("video/")) {
      toast.error("Please choose a video file (mp4, mov, webm)");
      return;
    }
    const maxBytes = 2 * 1024 * 1024 * 1024; // 2 GB
    if (file.size > maxBytes) {
      toast.error("File is larger than 2 GB");
      return;
    }

    setUploadingSlug(lesson.slug);
    setProgress(0);

    const ext = file.name.split(".").pop() || "mp4";
    const path = `${lesson.slug}/${Date.now()}.${ext}`;

    // Remove previous file if present
    if (lesson.video_path) {
      await supabase.storage.from("lesson-videos").remove([lesson.video_path]);
    }

    const { error: uploadError } = await supabase.storage
      .from("lesson-videos")
      .upload(path, file, {
        contentType: file.type,
        upsert: false,
      });

    if (uploadError) {
      toast.error(`Upload failed: ${uploadError.message}`);
      setUploadingSlug(null);
      return;
    }

    const { error: updateError } = await supabase
      .from("lessons")
      .update({ video_path: path })
      .eq("slug", lesson.slug);

    if (updateError) {
      toast.error(`Saved video but failed to link: ${updateError.message}`);
      setUploadingSlug(null);
      return;
    }

    toast.success(`${lesson.title} uploaded`);
    setUploadingSlug(null);
    setProgress(0);
    await loadLessons();
  };

  const handleRemove = async (lesson: LessonRow) => {
    if (!lesson.video_path) return;
    if (!confirm(`Remove uploaded video for "${lesson.title}"?`)) return;
    await supabase.storage.from("lesson-videos").remove([lesson.video_path]);
    const { error } = await supabase
      .from("lessons")
      .update({ video_path: null })
      .eq("slug", lesson.slug);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Video removed");
    await loadLessons();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-gold/10 bg-background/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="container mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/admin" className="flex items-center gap-3">
            <img src={wtaLogo} alt="WTA" className="w-8 h-8 object-contain" />
            <span className="font-display text-lg font-semibold text-gradient-gold">Admin — Lessons</span>
          </Link>
          <Link to="/admin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold font-body text-sm">
            <ArrowLeft size={16} /> Back to admin
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-10 md:py-14 max-w-4xl space-y-8">
        <div className="space-y-2">
          <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground">Lesson Videos</h1>
          <p className="text-muted-foreground font-body">
            Upload each lesson video directly to your portal. Files stream from Lovable Cloud —
            never sent to YouTube. Students see them without any external branding.
          </p>
        </div>

        <div className="space-y-4">
          {lessons.map((lesson) => {
            const hasNativeVideo = !!lesson.video_path;
            const isUploading = uploadingSlug === lesson.slug;
            return (
              <div key={lesson.slug} className="border border-gold/20 bg-card/60 p-6 space-y-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-gold font-body">
                      {lesson.order_index === 0 ? "Module" : `Lesson ${lesson.order_index}`}
                    </p>
                    <h2 className="font-display text-xl font-semibold text-foreground mt-1">
                      {lesson.title}
                    </h2>
                    <div className="mt-2 text-xs font-body flex items-center gap-2">
                      {hasNativeVideo ? (
                        <>
                          <CheckCircle2 size={14} className="text-gold" />
                          <span className="text-gold">Hosted here</span>
                        </>
                      ) : lesson.video_url ? (
                        <>
                          <Video size={14} className="text-muted-foreground" />
                          <span className="text-muted-foreground">
                            Currently falling back to external link — upload to replace
                          </span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">No video yet</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <label
                      className={`inline-flex items-center gap-2 px-4 py-2 border font-body text-xs font-semibold uppercase tracking-[0.2em] cursor-pointer ${
                        isUploading
                          ? "border-gold/20 text-muted-foreground cursor-wait"
                          : "border-gold/40 text-gold hover:bg-gold/10"
                      }`}
                    >
                      <Upload size={14} />
                      {isUploading ? "Uploading…" : hasNativeVideo ? "Replace" : "Upload"}
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        disabled={isUploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUpload(lesson, f);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {hasNativeVideo && !isUploading && (
                      <button
                        onClick={() => handleRemove(lesson)}
                        className="p-2 border border-destructive/40 text-destructive hover:bg-destructive/10"
                        title="Remove uploaded video"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground font-body">
          Tip: MP4 (H.264 + AAC) plays everywhere. Keep files under ~500 MB per lesson for
          smooth streaming.
        </p>
      </main>
    </div>
  );
};

export default AdminLessons;
